import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { brand } from '../../theme';

// Visual countdown to the festival. We re-export this so other pages can
// reuse the same numerals if needed. Target is supplied by the parent so
// the festival date isn't duplicated across files.
const Countdown = ({ targetDate, compact = false }) => {
  const [timeLeft, setTimeLeft] = useState(() => computeTimeLeft(targetDate));

  useEffect(() => {
    const tick = () => setTimeLeft(computeTimeLeft(targetDate));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetDate]);

  const blocks = [
    { value: timeLeft.days, label: 'Days' },
    { value: timeLeft.hours, label: 'Hours' },
    { value: timeLeft.minutes, label: 'Minutes' },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        gap: { xs: 2.5, sm: 4 },
        py: compact ? 1 : 2,
      }}
    >
      {blocks.map((block, idx) => (
        <React.Fragment key={block.label}>
          <Block value={block.value} label={block.label} compact={compact} />
          {idx < blocks.length - 1 && <Divider compact={compact} />}
        </React.Fragment>
      ))}
    </Box>
  );
};

const Block = ({ value, label, compact }) => (
  <Box sx={{ textAlign: 'center', minWidth: compact ? 56 : 72 }}>
    <Typography
      component="div"
      className="tabular-nums"
      sx={{
        fontFamily: '"Fraunces", Georgia, serif',
        fontWeight: 600,
        color: brand.navy,
        fontSize: compact
          ? { xs: '2rem', sm: '2.5rem' }
          : { xs: '3rem', sm: '4rem' },
        lineHeight: 1,
        letterSpacing: '-0.02em',
      }}
    >
      {String(value).padStart(2, '0')}
    </Typography>
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        mt: 0.75,
        color: brand.textSecondary,
        fontSize: compact ? '0.75rem' : '0.875rem',
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
      }}
    >
      {label}
    </Typography>
  </Box>
);

const Divider = ({ compact }) => (
  <Box
    aria-hidden
    sx={{
      // Vertically aligned with the numerals, not the labels, so the
      // hairline sits at the optical center of the row.
      mb: compact ? '20px' : { xs: '24px', sm: '28px' },
      width: 1,
      height: compact ? 32 : { xs: 40, sm: 56 },
      backgroundColor: brand.hairline,
    }}
  />
);

function computeTimeLeft(targetDate) {
  const diff = targetDate.getTime() - Date.now();
  if (diff <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  }
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / 1000 / 60) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
}

export default Countdown;
